import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';

import {
  DD_RUM_APPLICATION_ID,
  DD_RUM_CLIENT_TOKEN,
  DD_ENV,
  DD_VERSION,
  DD_PROXY_PATH,
} from './config.js';

// Email credentials: token on auth links and t in the public offer fragment.
// Redact both everywhere, including encoded URLs quoted in errors and logs.
export const SENSITIVE_PARAMS = ['token', 't'];

const NAMES = SENSITIVE_PARAMS.join('|');

// A param separator or `=`, literal or percent-encoded (once or more:
// %3F, %253F, ...). Covers ?token=abc, &token=abc, #token=abc and the same
// param inside another param's value (redirect=%2Fx%3Ftoken%3Dabc%26tab%3Dx),
// mixed forms included.
const SEPARATOR = '[?&#]|%(?:25)*(?:3F|26|23)';
const EQUALS = '=|%(?:25)*3D';
const SENSITIVE_PARAM_RE = new RegExp(
  // The value ends at the next separator, literal or encoded.
  `(${SEPARATOR})(${NAMES})(${EQUALS})(?:(?!%(?:25)*(?:26|23))[^&#\\s"'<>():;,])*`,
  'gi',
);

/**
 * Replace the value of every sensitive query param in `value` with REDACTED,
 * keeping the rest of the URL. Works on URLs, relative URLs and free text that
 * quotes a URL (error messages, stacks). Pure, and never throws: anything that
 * is not a string comes back as is. A beforeSend that throws does not drop the
 * event: SDK 7.0.0 catches the error (catchUserErrors) and sends the event
 * without redacting it.
 */
export function redactSensitiveParams(value) {
  if (typeof value !== 'string') return value;
  try {
    return value.replace(SENSITIVE_PARAM_RE, '$1$2$3REDACTED');
  } catch {
    return value;
  }
}

function redactField(obj, key) {
  if (obj && typeof obj[key] === 'string') {
    obj[key] = redactSensitiveParams(obj[key]);
  }
}

// Fields beforeSend may modify in RUM (view.*, resource.url, error.* and
// long_task.scripts[]; see modifiableFieldPathsByEvent in
// @datadog/browser-rum-core). Always sends the event.
function rumBeforeSend(event) {
  try {
    redactField(event.view, 'url');
    redactField(event.view, 'referrer');
    redactField(event.view?.performance?.lcp, 'resource_url');
    redactField(event.resource, 'url');
    redactField(event.error, 'message');
    redactField(event.error, 'stack');
    redactField(event.error, 'handling_stack');
    redactField(event.error?.resource, 'url');
    if (Array.isArray(event.long_task?.scripts)) {
      event.long_task.scripts.forEach((script) => {
        redactField(script, 'source_url');
        redactField(script, 'invoker');
      });
    }
  } catch {
    // Keep the event even if it could not be cleaned.
  }
  return true;
}

// Browser Logs lets beforeSend mutate the whole log.
function logsBeforeSend(log) {
  try {
    redactField(log, 'message');
    redactField(log.view, 'url');
    redactField(log.view, 'referrer');
    redactField(log.http, 'url');
    redactField(log.error, 'message');
    redactField(log.error, 'stack');
    if (Array.isArray(log.error?.causes)) {
      log.error.causes.forEach((cause) => {
        redactField(cause, 'message');
        redactField(cause, 'stack');
      });
    }
  } catch {
    // Keep the log even if it could not be cleaned.
  }
  return true;
}

let rumInitialized = false;
let replayStopped = null;
let popstateListening = false;

function isSensitive(value) {
  return typeof value === 'string' && redactSensitiveParams(value) !== value;
}

function carriesSensitiveParam(search) {
  // The offer page retains its credential in memory after cleaning the URL.
  // Its content must never resume replay while this route remains visible.
  return (
    window.location.pathname.replace(/\/$/, '') === '/oferta' ||
    isSensitive(search) ||
    isSensitive(window.location.href)
  );
}

/**
 * The session replay Meta record carries window.location.href and does not go
 * through beforeSend, so the recording is stopped while the URL carries a
 * sensitive param and started again once it does not.
 *
 * This is the half that is safe to run while rendering: it only cuts, never
 * resumes, so a render React abandons cannot restart the recording. It looks at
 * `search` and at window.location.href, because a late render of a clean route
 * can run after the history already went back to a URL with a token. The SDK
 * stop is idempotent and is called every time, so a stop that failed once is
 * retried. No effect before RUM is initialized. Never throws.
 */
export function stopSessionReplayIfSensitive(search) {
  if (!rumInitialized) return;
  try {
    if (!carriesSensitiveParam(search)) return;
    replayStopped = true;
    datadogRum.stopSessionReplayRecording();
  } catch {
    // The replay state is best effort.
  }
}

/**
 * Full sync: stops if `search` or window.location.href carries a sensitive
 * param, resumes only if neither does. It runs after the route commits (and
 * from initDatadog), when window.location is the page actually on screen.
 * Only the start is guarded by `replayStopped`; the cut always reaches the SDK.
 * No effect before RUM is initialized. Never throws.
 */
export function syncSessionReplay(search) {
  if (!rumInitialized) return;
  try {
    if (carriesSensitiveParam(search)) {
      replayStopped = true;
      datadogRum.stopSessionReplayRecording();
    } else if (replayStopped !== false) {
      replayStopped = false;
      datadogRum.startSessionReplayRecording();
    }
  } catch {
    // The replay state is best effort.
  }
}

/**
 * Initialize Datadog RUM (Real User Monitoring) and Browser Logs.
 *
 * The application ID is non-secret and is checked into the build env.
 * The client token is bundled into the JS at build time but is treated
 * as low-sensitivity per Datadog's RUM model. It is injected via env
 * (see stg.yml / main.yml) and is therefore absent in local dev unless
 * explicitly configured — in which case RUM stays disabled.
 *
 * Router-level page-view tracking is enabled via
 * `reactPlugin({ router: true })`, which works in tandem with the
 * Datadog-wrapped `Routes` re-exported from
 * `@datadog/browser-rum-react/react-router-v7` (see `src/app/Routes.js`).
 * Each route render emits a RUM view span; without the wrapped
 * `Routes`, the plugin would no-op and we would only get errors,
 * resources, long tasks, and user interactions.
 */
export function initDatadog() {
  // Require all three explicitly. Missing DD_ENV would mis-tag prod
  // traffic as `development` if a CI build forgets the injection —
  // refuse to init instead. Local-dev RUM testing should set DD_ENV
  // explicitly in .env to opt in.
  const enabled =
    Boolean(DD_RUM_APPLICATION_ID) &&
    Boolean(DD_RUM_CLIENT_TOKEN) &&
    Boolean(process.env.DD_ENV);
  if (!enabled) {
    return;
  }

  // Same-origin proxy avoids client-side ad blockers that filter the
  // `*.datadoghq.com` intake hostnames. The string form replaces
  // `https://[intake].browser-intake-datadoghq.com` in the request URL;
  // the SDK still appends `/api/v2/{rum,logs}?...`. A CloudFront behavior
  // must forward `${DD_PROXY_PATH}/*` to `browser-intake-datadoghq.com/*`.
  // See https://docs.datadoghq.com/real_user_monitoring/guide/proxy-rum-data/
  const proxy = DD_PROXY_PATH || undefined;

  datadogRum.init({
    applicationId: DD_RUM_APPLICATION_ID,
    clientToken: DD_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'recepthor-web',
    env: DD_ENV,
    version: DD_VERSION || undefined,
    proxy,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    // Started by syncSessionReplay once the URL is known to carry no secret.
    startSessionReplayRecordingManually: true,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
      // Match any recepthor-api host (alpha/beta/prod) for trace linking.
      // Anchored with ^ and a trailing boundary so hosts like
      // `api.recepthor.cl.evil.com` do not match.
      /^https:\/\/[a-z0-9-]+\.recepthor\.cl(\/|$)/,
    ],
    plugins: [reactPlugin({ router: true })],
    beforeSend: rumBeforeSend,
  });
  rumInitialized = true;
  // History navigation changes the URL before the router renders. Cutting on
  // popstate, which runs before the router's own listener, closes the gap in
  // which a pending start could take its first snapshot on a URL with a token.
  // It only cuts.
  if (!popstateListening) {
    popstateListening = true;
    window.addEventListener('popstate', () =>
      stopSessionReplayIfSensitive(window.location.href),
    );
  }
  syncSessionReplay(window.location.search);

  datadogLogs.init({
    clientToken: DD_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'recepthor-web',
    env: DD_ENV,
    proxy,
    forwardErrorsToLogs: true,
    // 'error' is intentionally excluded — forwardErrorsToLogs already
    // captures uncaught errors. Forwarding console.error duplicates them
    // and risks shipping axios error payloads (with auth headers) to DD.
    forwardConsoleLogs: ['warn'],
    sessionSampleRate: 100,
    beforeSend: logsBeforeSend,
  });
}

export function setDatadogUser({ id, email, firstName, lastName }) {
  datadogRum.setUser({
    id,
    email,
    name: `${firstName} ${lastName ?? ''}`.trim(),
  });
}

export function clearDatadogUser() {
  datadogRum.clearUser();
}
